/**
 * Default configuration for FIDO2-JS SDK
 *
 * Provides sensible defaults including externalized AAGUID for UltraPass
 * authenticator and configurable API endpoints.
 *
 * @module config/defaults
 */

import { SDKConfig } from '../types/config';

/**
 * Verify endpoint per environment.
 *
 * The path is `/v4/fido2/verify`. Earlier `/verify` and `/v2/verify` paths are superseded.
 *
 * Only the public production service has a default. Non-production deployments are
 * environment-specific and are deliberately not baked into this package — point
 * `apiEndpoints.verify` at your own backend, or set `FIDO2_VERIFY_URL`.
 *
 * **The verify host must match the backend the authenticator is configured against.** The JWE
 * is minted under the policy that host served, so verifying it elsewhere checks the proof
 * against the wrong policy and fails after registration has already succeeded.
 */
export const VERIFY_ENDPOINTS = {
  production: 'https://fido2-server.privateid.com/v4/fido2/verify'
} as const;

/**
 * iOS UltraPass URL scheme per environment.
 *
 * Production and staging UltraPass are distinct apps installable side by side, each
 * registering its own scheme. The scheme must match the build on the device, and pairs with
 * the backend that build talks to.
 *
 * Non-production defaults to the staging app.
 */
export const ULTRAPASS_SCHEMES = {
  development: 'ultrapass-staging',
  staging: 'ultrapass-staging',
  production: 'ultrapass'
} as const;

/**
 * Default SDK configuration
 *
 * This configuration externalizes the previously hardcoded AAGUID from
 * webauthn.js (lines 57-60) and provides environment variable support
 * for API endpoints.
 *
 * Users can override any of these values when creating the SDK instance.
 *
 * @example
 * ```typescript
 * const sdk = new FIDO2SDK({
 *   apiKey: 'your-api-key',
 *   authenticators: {
 *     ultrapass: {
 *       aaguid: 'custom-aaguid',
 *       name: 'Custom UltraPass'
 *     }
 *   }
 * });
 * ```
 */
export const DEFAULT_CONFIG: Omit<SDKConfig, 'apiKey'> = {
  /**
   * Supported authenticators with their AAGUIDs
   *
   * The AAGUID is extracted from the authenticator during registration
   * and verified against these configured values.
   */
  authenticators: {
    /**
     * UltraPass authenticator configuration
     *
     * Both the Windows HID and plugin transports advertise the same value, because the plugin
     * sources it from the service's `authenticatorGetInfo`.
     *
     * Two values are in the field, and which one is correct depends on the platform:
     *
     * - `80:48:2e:30:...:b1` — a fresh RFC 4122 v4 value from the July 2026 firmware revision,
     *   shipped in the **1.0.13 Windows** build and since rolled out to the mobile apps.
     * - `00:76:63:1b:...:79` — what every build up to and including 1.0.11 reported.
     *
     * Every platform currently accepts **both**, because both builds are in the field and a
     * device sending the value its platform was not listed for is rejected outright — that is
     * how this surfaced: an Android staging registration on 2026-08-11 came back with the new
     * value against an Android list holding only the legacy one, and the ceremony failed after
     * the user had already completed face capture.
     *
     * Accepting both costs only the ability to tell the two builds apart; it cannot admit a
     * foreign authenticator, since both values are UltraPass. Observed status when this was
     * last checked: desktop on both, Android rotated (2026-08-11), iOS still sending legacy
     * (2026-08-10) — iOS carries the new value pre-emptively so its rotation is a non-event.
     *
     * The per-platform split is kept rather than collapsed into one list: it is the mechanism
     * for tightening a platform back down to a single value once every install there has
     * rotated, which is worth doing per platform as each one finishes.
     */
    ultrapass: {
      // Fallback for platforms not named below (including 'Unknown').
      aaguid: '00:76:63:1b:d4:a0:42:7f:57:73:0e:c7:1c:9e:02:79',
      name: 'UltraPass',
      platformAaguids: {
        ios: [
          '80:48:2e:30:06:9e:4d:f9:b5:3e:2f:ce:89:59:6b:b1',
          '00:76:63:1b:d4:a0:42:7f:57:73:0e:c7:1c:9e:02:79'
        ],
        android: [
          '80:48:2e:30:06:9e:4d:f9:b5:3e:2f:ce:89:59:6b:b1',
          '00:76:63:1b:d4:a0:42:7f:57:73:0e:c7:1c:9e:02:79'
        ],
        desktop: [
          '80:48:2e:30:06:9e:4d:f9:b5:3e:2f:ce:89:59:6b:b1',
          '00:76:63:1b:d4:a0:42:7f:57:73:0e:c7:1c:9e:02:79'
        ]
      }
    }
  },

  /**
   * Backend API endpoint for JWE token verification
   *
   * Current: /v4/fido2/verify — matches the native iOS and Android launchers.
   * Superseded: /verify, /v2/verify.
   *
   * Defaults to the public production service. For any other deployment, set this
   * explicitly or export FIDO2_VERIFY_URL.
   *
   * Supports environment variable: FIDO2_VERIFY_URL
   */
  apiEndpoints: {
    verify:
      (typeof process !== 'undefined' && process.env?.FIDO2_VERIFY_URL) ||
      VERIFY_ENDPOINTS.production
  },

  /**
   * Target environment — selects the default verify endpoint.
   *
   * Only 'production' has a built-in endpoint. 'development' and 'staging' require an
   * explicit `apiEndpoints.verify` (or FIDO2_VERIFY_URL).
   */
  environment: 'production',

  /**
   * Request largeBlob support at registration, matching native `.supportRequired`.
   *
   * Without this the biometric-proof JWE cannot be read back during authentication.
   */
  largeBlobSupport: 'required',

  /**
   * Declare blob support with the WebAuthn L3 `largeBlob` extension, matching the current
   * launcher (`registrationRequest.largeBlob = .supportRequired`).
   */
  largeBlobExtension: 'largeBlob',

  /**
   * Which iOS deeplink host authentication uses.
   *
   * 'start' is the current unified entry point and the default. 'legacy-verify' is kept as an
   * escape hatch for older UltraPass builds that predate the `start` host.
   */
  iosAuthPath: 'start',

  /**
   * Omit allowCredentials on the Phase 2 assertion and rely on resident-key discovery.
   *
   * This was `true`, justified by "registration uses residentKey: 'preferred', so the credential
   * may not be discoverable". **That premise no longer holds** — `createCredential` now requests
   * `residentKey: 'required'` (and `requireResidentKey: true`), so every credential this SDK
   * creates is discoverable and discovery cannot come up empty for lack of a resident key.
   *
   * With the premise gone, the cost stands on its own: Phase 1 already captured the biometric
   * inside the UltraPass app, and naming a credential explicitly makes the provider re-verify —
   * surfacing a second face prompt, or refusing outright with `NotAllowedError`. An iOS log from
   * 2026-08-04 shows exactly that: enrollment succeeded, then Phase 2 sent one allowCredential
   * and Safari returned "The request is not allowed by the user agent or the platform in the
   * current context" in 5.4s.
   *
   * Set to `true` to restore the old behaviour if a discovery-only assertion finds nothing —
   * which would mean the credential is not resident after all, and is worth investigating rather
   * than papering over.
   */
  phase2AllowCredentials: false,

  /**
   * Ceremony options that diverge between the deeplink flows and the desktop/Windows flow.
   *
   * All four default to 'auto', meaning: match the PingFederate adapter's templates on desktop
   * (no PRF, no credProtect keys, no transports hint, AbortController-enforced timeout) and keep
   * the deeplink behaviour on iOS/Android. Resolved in `FIDO2SDK.resolveCeremonyOptions()`.
   */
  prfExtension: 'auto',
  credProtectExtension: 'auto',
  allowCredentialsTransports: 'auto',
  enforceTimeoutWithAbort: 'auto',

  /**
   * Policy ID for authenticator configuration
   * Used for mobile deeplink flows
   *
   * IMPORTANT: This must match the policy configured in your backend.
   * Common values: 'mobile', 'default', 'ultrapass-mobile'
   * Contact your backend team to get the correct value.
   */
  policyId: 'mobile',

  /**
   * Default timeout for WebAuthn operations (60 seconds)
   */
  timeout: 60000,

  /**
   * Debug mode disabled by default
   */
  debug: false,

  /**
   * Mobile callback delay (5 seconds)
   *
   * CRITICAL for iOS Safari: After deeplink redirect, Safari needs time to render
   * before WebAuthn can be triggered. This delay ensures reliable Phase 2 WebAuthn
   * completion, especially on first-time flows when Safari is initializing.
   *
   * Increased to 5000ms to handle first-time callback scenarios where Safari
   * needs more time to be ready for WebAuthn. Second+ attempts work faster
   * because Safari is already initialized.
   *
   * @see iOS_IMPLEMENTATION_COMPARISON.md for detailed explanation
   */
  mobileCallbackDelay: 5000,

  /**
   * Android init deeplink delay (1 second)
   *
   * Time between firing `privateid://init` and starting the WebAuthn ceremony. Android runs
   * both in the same page, so this wait is all the UltraPass app gets to register itself as a
   * credential provider. Too short against a cold start surfaces as `NotReadableError` from
   * Android's Credential Manager.
   */
  androidInitDelay: 1000
};

/**
 * Helper function to merge user configuration with defaults
 *
 * @param userConfig - User-provided configuration (may be partial)
 * @returns Complete SDK configuration
 *
 * @example
 * ```typescript
 * const config = mergeConfig({ apiKey: 'key', timeout: 30000 });
 * // Returns: { ...DEFAULT_CONFIG, apiKey: 'key', timeout: 30000 }
 * ```
 */
export function mergeConfig(userConfig: Partial<SDKConfig> & { apiKey: string }): SDKConfig {
  const environment = userConfig.environment || DEFAULT_CONFIG.environment || 'production';

  // Endpoint precedence: explicit user value > FIDO2_VERIFY_URL env var > environment default.
  // Only 'production' has a built-in host. Non-production deployments differ per installation
  // and are not shipped in this package, so they must be supplied by the caller — failing
  // loudly here beats silently verifying against the wrong backend.
  const envVarEndpoint =
    typeof process !== 'undefined' ? process.env?.FIDO2_VERIFY_URL : undefined;
  const environmentEndpoint =
    environment === 'production' ? VERIFY_ENDPOINTS.production : undefined;
  const verify = userConfig.apiEndpoints?.verify || envVarEndpoint || environmentEndpoint;

  if (!verify) {
    throw new Error(
      `No verify endpoint for environment '${environment}'. Only 'production' has a built-in ` +
        'endpoint; set apiEndpoints.verify (or the FIDO2_VERIFY_URL environment variable) to ' +
        'the /v4/fido2/verify URL of the backend your authenticator is configured against.'
    );
  }

  // Same precedence for the iOS app scheme: explicit value, else the environment's app.
  const ultrapassScheme = userConfig.ultrapassScheme || ULTRAPASS_SCHEMES[environment];

  return {
    ...DEFAULT_CONFIG,
    ...userConfig,
    environment,
    ultrapassScheme,
    apiEndpoints: {
      ...DEFAULT_CONFIG.apiEndpoints,
      ...(userConfig.apiEndpoints || {}),
      verify
    },
    authenticators: {
      ...DEFAULT_CONFIG.authenticators,
      ...(userConfig.authenticators || {})
    }
  };
}
